import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { FieldSet, Label, LabeledControl, Legend, Radio, Toast, useToast } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_laundry } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { setAmenityField } from '../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import { KeyFeaturesConstants, LAUNDRY_FIELD_NAME_BY_TYPE } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

const { RADIO_OPTIONS } = KeyFeaturesConstants;

const LaundryAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const laundry = listingDetails?.laundry || 'None';
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localLaundry, setLocalLaundry] = useState(laundry);

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateLaundry } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_laundry,
    applyToCache: ({ postData }) =>
      postData.laundry ? [setAmenityField(LAUNDRY_FIELD_NAME_BY_TYPE[postData.laundry], true)] : [],
    successToastMessage: 'Laundry updated',
    meta: { context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_laundry', details: { listingAlias } } },
  });

  const save = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldValue = event.target.value;
    const previousValue = localLaundry;
    const laundryType = RADIO_OPTIONS?.[fieldValue].key;

    setLocalLaundry(fieldValue);

    try {
      if (isFeedListing && laundryType) {
        const previousType = RADIO_OPTIONS?.[previousValue]?.key;
        const previousFieldName = previousType ? LAUNDRY_FIELD_NAME_BY_TYPE[previousType] : undefined;
        const nextFieldName = LAUNDRY_FIELD_NAME_BY_TYPE[laundryType];
        // Save the new flag before clearing the old one so a partial failure never leaves every option off.
        await setFeedAmenity(nextFieldName, true);
        if (previousFieldName && previousFieldName !== nextFieldName) {
          await setFeedAmenity(previousFieldName, false);
        }
        enqueueToast(<Toast tone="success">Laundry updated</Toast>);
      } else {
        await updateLaundry({
          postData: {
            laundry: laundryType,
            listingAlias,
          },
        });
      }
    } catch (err) {
      setLocalLaundry(previousValue);
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Laundry</Legend>} disabled={isInputFormDisabled}>
      {RADIO_OPTIONS &&
        Object.keys(RADIO_OPTIONS).map((fieldValue) => (
          <LabeledControl
            key={`laundryTypes-${RADIO_OPTIONS[fieldValue].label}`}
            label={<Label>{RADIO_OPTIONS[fieldValue].label}</Label>}
            control={
              <Radio
                name="LabeledControl-Laundry"
                value={fieldValue}
                checked={localLaundry === fieldValue}
                onChange={save}
              />
            }
          />
        ))}
    </FieldSet>
  );
};

export default memo(LaundryAmenities);
